// « Préparer la facture » depuis les frais et débours cochés : plan et conversions, sans réseau.
//   frais   → lignes de nature « frais » d'un brouillon de facture CIME (avec TVA) ;
//   débours → lignes d'un brouillon de facture de débours Polaris (hors TVA, TTC exact).

export interface PreparableExpense {
  id: string;
  dossier_id: string;
  nature: string;
  label: string;
  supplier: string | null;
  piece_number: string | null;
  piece_date: string;
  amount_ht: number | string;
  vat_rate: number | string;
  amount_ttc: number | string;
  receipt_path: string | null;
  billed_at: string | null;
}

export type PreparationPlan<T extends PreparableExpense> =
  | { ok: false; reason: string }
  | { ok: true; dossierId: string; frais: T[]; debours: T[]; missingReceipts: number; totalTtc: number };

export function planPreparation<T extends PreparableExpense>(selection: T[]): PreparationPlan<T> {
  if (selection.length === 0) return { ok: false, reason: 'Cochez au moins une ligne.' };
  const dossiers = new Set(selection.map((e) => e.dossier_id));
  if (dossiers.size > 1) {
    return { ok: false, reason: 'Les lignes cochées doivent appartenir au même dossier : une facture par dossier.' };
  }
  if (selection.some((e) => e.billed_at)) return { ok: false, reason: 'Une des lignes cochées est déjà refacturée.' };
  const debours = selection.filter((e) => e.nature === 'debours');
  return {
    ok: true,
    dossierId: selection[0]!.dossier_id,
    frais: selection.filter((e) => e.nature === 'frais'),
    debours,
    missingReceipts: debours.filter((e) => !e.receipt_path).length,
    totalTtc: selection.reduce((sum, e) => sum + Math.round(Number(e.amount_ttc) * 100), 0) / 100,
  };
}

function frDate(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
}

export function fraisToInvoiceLines(frais: PreparableExpense[]) {
  return frais.map((e, i) => ({
    description: `${e.label} (${frDate(e.piece_date)})`,
    quantity: 1,
    unit: 'forfait' as const,
    unit_price: Number(e.amount_ht),
    sort_order: i + 1,
    nature: 'frais' as const,
    vat_rate: Number(e.vat_rate),
  }));
}

/** Clé de rapprochement d'un fournisseur Polaris par son nom. */
export function supplierKey(name: string): string {
  return name.trim().replace(/\s+/g, ' ').toLowerCase();
}

export function deboursToPolarLines(debours: PreparableExpense[], supplierIds: Map<string, number>) {
  return debours.map((e) => {
    const id = supplierIds.get(supplierKey(e.supplier ?? ''));
    if (!id) throw new Error(`Fournisseur introuvable dans Polaris : ${e.supplier ?? '—'}`);
    return {
      supplier_id: id,
      number: e.piece_number,
      piece_date: e.piece_date.slice(0, 10),
      ttc_cents: Math.round(Number(e.amount_ttc) * 100),
    };
  });
}
